// frontend/packages/business-console/src/pages/BillingSettings.jsx
//
// Item 3 — tenant-facing Billing & Reminders settings. The first surface on
// the new PATCH /businesses/settings endpoint.
//
// Today it holds one control: the overdue-invoice reminder kill switch
// (`settings.invoiceRemindersEnabled`). Reminders are ON by default — the
// backend sweep treats an absent value as enabled — so the toggle reflects
// `!== false`. Future billing settings (review-request delay, etc.) land on
// this page.
//
// Role: the endpoint is businessadmin-only server-side. Like
// StripeConnectSettings, the AuthContext doesn't expose role client-side yet,
// so we render the control for everyone and surface the server's 403 message
// if a non-admin tries to change it (then revert the optimistic toggle).

import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import { updateBusinessSettings } from '../services/businessService';
import AiUsageMeter from '../components/AiUsageMeter';
import BillingHistory from '../components/BillingHistory';
import SubscriptionCancel from '../components/SubscriptionCancel';
import { useTokens, PageHeader, Surface } from '../components/primitives';

export default function BillingSettings() {
  const t = useTokens();
  const { business, loading, refreshBusiness } = useContext(BusinessContext);

  // Default-ON: only an explicit `false` disables reminders.
  const remindersEnabled = useMemo(
    () => business?.settings?.invoiceRemindersEnabled !== false,
    [business]
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);

  const handleToggleReminders = async (event) => {
    const next = event.target.checked;
    setSaving(true);
    setError(null);
    try {
      await updateBusinessSettings({ invoiceRemindersEnabled: next });
      // Re-pull the business doc so the toggle reflects persisted state.
      await refreshBusiness();
      setSavedMsg(
        next
          ? 'Overdue-invoice reminders turned on.'
          : 'Overdue-invoice reminders turned off.'
      );
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      setError(
        status === 403
          ? serverMsg || 'Only business admins can change reminder settings.'
          : serverMsg || 'Could not save. Please try again.'
      );
      // No optimistic state to revert — the Switch reads from `business`,
      // which is unchanged until refreshBusiness() succeeds.
    } finally {
      setSaving(false);
    }
  };

  if (loading && !business) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 880, mx: 'auto', width: '100%' }}>
      <PageHeader title="Billing &amp; reminders" sx={{ mb: 0.75 }} />
      <Typography sx={{ fontSize: 13.5, color: t.muted, mb: 3 }}>
        Your plan usage, billing history, and automated billing communications
        for your customers.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <AiUsageMeter variant="card" />
      </Box>

      <Box sx={{ mb: 3 }}>
        <BillingHistory />
      </Box>

      <Box sx={{ mb: 3 }}>
        <SubscriptionCancel />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <NotificationsActiveOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', mt: 0.5 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormControlLabel
              sx={{ m: 0, alignItems: 'center' }}
              control={
                <Switch
                  checked={remindersEnabled}
                  onChange={handleToggleReminders}
                  disabled={saving}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Overdue-invoice reminders
                </Typography>
              }
            />
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              When an invoice goes past due, automatically email the customer a
              friendly reminder with a payment link at 1, 7, and 14 days overdue
              (3 reminders, then it stops). Turn this off to handle follow-ups
              yourself.
            </Typography>
            {saving ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Saving…
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Reminders are sent from your business&apos;s email and respect your
          customers&apos; contact details. Only admins can change this setting.
        </Typography>
      </Surface>

      <Snackbar
        open={Boolean(savedMsg)}
        autoHideDuration={3000}
        onClose={() => setSavedMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSavedMsg(null)}
        >
          {savedMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
